import { cxWindow} from "../interfaces";
import { Inject, Injectable } from "@angular/core";
import { WINDOW } from "@tylertech/shared-module";
import { RouteInfoService } from "./routeInfo.service";

interface EscalationResult
{
    success: boolean;
    message: string;
    error: string;
    escalationSummary: string;
    timestamp: string;
    receivedArgs: any;
}

@Injectable()
export class CxService
{
    readonly BrandId: string = "4189";
    readonly ChannelId: string = "chat_2c51ff34-f856-41ee-8da5-e68947f002f8";
    readonly Script: string = "https://web-modules-de-na1.niceincontact.com/loader/1/loader.js";

    constructor(
        @Inject(WINDOW) private readonly window: cxWindow,
        private $routeInfoService: RouteInfoService
    ) { }

    initialize(): void
    {
        this.window.debugClientBridge = () =>
        {
            if (this.window.ClientBridge && this.window.ClientBridge.getClientBridge)
                return this.window.ClientBridge.getClientBridge();
        };

        this.doInitialize();
    }

    private async doInitialize(): Promise<void>
    {
        await this.waitForClientBridge();
        this.initializeEscalateTool();
    }

    private waitForClientBridge(): Promise<void>
    {
        return new Promise(resolve =>
        {
            const check = () =>
            {
                if (this.window.ClientBridge && typeof this.window.ClientBridge.getClientBridge === "function")
                    resolve();
                else
                    requestAnimationFrame(check);
            };
            check();
        });
    }

    private initializeEscalateTool(): void
    {
        this.window.ClientBridge.getClientBridge().registerTool(this.escalateTool, this.executeEscalate.bind(this));
    }

    private async executeEscalate(args: any): Promise<EscalationResult>
    {
        // args contains: reason, priority, conversationSummary, etc.
        console.log("Tool called with:", args);

        if (!args || typeof args !== "object")
        {
            console.error("[Escalate Tool] Invalid args received:", args);
            return <EscalationResult>{
                success: false,
                message: "Invalid arguments received",
                error: "Args must be an object"
            };
        }

        if (!args.reason || !args.conversationSummary)
        {
            console.error("[Escalate Tool] Missing required fields. Received:", args);
            return <EscalationResult>{
                success: false,
                message: "Missing required fields: reason and conversationSummary are required",
                error: "Missing required fields",
                receivedArgs: args
            };
        }

        try
        {
            const escalationMessage = this.buildEscalationMessage(args);

            const screenWidth = window.screen?.availWidth ?? 1200;
            const screenHeight = window.screen?.availHeight ?? 800;
            const popupWidth = Math.floor(screenWidth / 2);
            const popupHeight = Math.floor(screenHeight * .9);
            const popupLeft = Math.max(0, screenWidth - popupWidth);
            const popupTop = 0;
            const features = `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},resizable=yes,scrollbars=yes`;

            const escalationData = {
                message: escalationMessage,
                brandId: this.window.BRAND_ID || this.BrandId,
                channelId: this.window.CHANNEL_ID || this.ChannelId,
                reason: args.reason,
                priority: args.priority,
                timestamp: new Date().toISOString()
            };
            sessionStorage.setItem("escalationData", JSON.stringify(escalationData));

            const chatWindow = window.open(`${window.location.origin}${this.$routeInfoService.baseUrl}/escalation-chat`, "CXoneChat", features);

            if (!chatWindow)
                throw new Error("Pop-up blocked. Please allow pop-ups for this site.");

            return <EscalationResult>{
                success: true,
                message: "Your conversation has been escalated to a human support agent. A chat window has been opened.",
                escalationSummary: escalationMessage,
                timestamp: new Date().toISOString()
            };

        }
        catch (error)
        {
            if (error.message && error.message.toLowerCase().includes("cookie"))
                this.showFallbackContact(args, "cookies");
             else
                this.showFallbackContact(args, "service_error");

            return <EscalationResult>{
                success: false,
                message: "Unable to open live chat. Alternative contact methods have been provided on the page.",
                error: error.message,
                escalationSummary: this.buildEscalationMessage(args)
            };
        }
    }

    private showFallbackContact(args, reason): void
    {
        const statusEl = document.getElementById("status");

        if (statusEl)
        {
            const cookieNoticeEl = document.getElementById("cookieNotice");
            const escalationMessage = this.buildEscalationMessage(args);
            if (reason === "cookies" && cookieNoticeEl)
                cookieNoticeEl.style.display = "block";

            statusEl.innerHTML = `
                <div class="fallback-contact">
                <h3>⚠️ Unable to Open Live Chat</h3>
                <p><strong>Reason:</strong> ${reason === "cookies" ? "Third-party cookies are disabled" : "Chat service unavailable"}</p>
                <p>Please use one of these alternative methods to reach support:</p>
                <ul>
                    <li><strong>Email:</strong> <a href="mailto:support@example.com?subject=Escalation%20Request&body=${encodeURIComponent(escalationMessage)}">support@example.com</a></li>
                    <li><strong>Phone:</strong> <a href="tel:+18005551234">1-800-555-1234</a></li>
                    <li><strong>Support Portal:</strong> <a href="https://support.example.com" target="_blank">https://support.example.com</a></li>
                </ul>
                <details>
                    <summary>View escalation details</summary>
                    <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; font-size: 12px; overflow-x: auto;">${escalationMessage}</pre>
                </details>
                </div>
            `;
        }
    }

    private buildEscalationMessage(args) : string
    {
        const timestamp = new Date().toLocaleString();
        const priority = args.priority || "medium";
        let message = `🔔 ESCALATED CASE - Priority: ${priority.toUpperCase()}\n\n`;
        message += `Reason: ${args.reason}\n\n`;
        if (args.context)
            message += `Context: ${args.context}\n\n`;
        if (args.conversationSummary)
            message += `Conversation Summary:\n${args.conversationSummary}\n\n`;
        message += `---\n`;
        message += `Escalated at: ${timestamp}\n`;
        message += `Page URL: ${window.location.href}\n`;
        message += `User Agent: ${navigator.userAgent}`;
        //inject extra information for userEmail, etc. here
        return message;
    }

    private readonly escalateTool = {
        name: "escalate",
        description: "Escalate the current conversation to a human support agent...",
        parameters: {
            type: "object",
            properties: {
                reason: {
                    type: "string",
                    description: "Brief reason for escalation"
                },
                priority: {
                    type: "string",
                    enum: ["low", "medium", "high", "urgent"],
                    description: "Priority level for the escalation"
                },
                conversationSummary: {
                    type: "string",
                    description: "REQUIRED: A detailed summary of the conversation..."
                }
            },
            required: ["reason", "conversationSummary"]
        }
    };

    initializeScript(): void
    {
        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.async = true;
        scriptElement.src = `${this.Script}?${Math.round(Date.now() / 1e3 / 3600)}`;
        document.head.appendChild(scriptElement);
    }

    initializeWindowVariables(message: string): void
    {
        this.window.CXoneDfo = "cxone";
        const placeholder: any = (...args: any[]) =>
        {
            (placeholder.q = placeholder.q || []).push(args);
        };

        placeholder.q = placeholder.q || [];
        placeholder.l = Date.now();
        placeholder.u = this.Script;
        this.window.cxone = placeholder;
        this.window.cxone("init", this.BrandId);
        this.window.cxone("guide", "init");
        this.window.cxone("chat", "init", +this.BrandId, this.ChannelId);
        this.window.cxone("chat", "setCustomerName", "Tyler AI User");
        this.window.cxone("chat", "setFirstAutomatedMessageContent", message);
        this.window.cxone("chat", "openChatWindow");
    }
}
